import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  MenuItem,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CropIcon from '@mui/icons-material/Crop';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ChangeEvent, useEffect, useState } from 'react';
import { http } from '../../api/http';
import type { Item } from './ItemsPage';
import { ImageCropDialog } from '../../components/ui/ImageCropDialog';
import { RichTextEditor } from '../../components/ui/RichTextEditor';

type ImageEntry = { type: 'url'; value: string } | { type: 'file'; value: File };

interface ItemFormDialogProps {
  open: boolean;
  onClose: (refresh?: boolean) => void;
  editingItem: Item | null;
}

interface ItemFormValues {
  title: string;
  description: string;
  categoryId: string;
  isActive: boolean;
}

interface Category {
  _id: string;
  name: string;
}

const schema = yup.object({
  title: yup.string().required('Title is required'),
  description: yup.string().required('Description is required'),
  categoryId: yup.string().required('Category is required'),
  isActive: yup.boolean().required()
});

export const ItemFormDialog = ({ open, onClose, editingItem }: ItemFormDialogProps) => {
  const [imageEntries, setImageEntries] = useState<ImageEntry[]>([]);
  const [videoFiles, setVideoFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [cropIndex, setCropIndex] = useState<number | null>(null);
  const [cropLoading, setCropLoading] = useState(false);
  const [dragImageIndex, setDragImageIndex] = useState<number | null>(null);
  const [dragVideoIndex, setDragVideoIndex] = useState<number | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<ItemFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      title: '',
      description: '',
      categoryId: '',
      isActive: true
    }
  });

  // load categories once
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await http.get<{ success: boolean; data: Category[] }>('/categories');
        setCategories(res.data.data);
      } catch {
        setCategories([]);
      }
    };
    void loadCategories();
  }, []);

  useEffect(() => {
    if (editingItem) {
      reset({
        title: editingItem.title,
        description: editingItem.description,
        categoryId: editingItem.categoryId || '',
        isActive: editingItem.isActive
      });
      setImageEntries((editingItem.images || []).map((url) => ({ type: 'url' as const, value: url })));
    } else {
      reset({
        title: '',
        description: '',
        categoryId: '',
        isActive: true
      });
      setImageEntries([]);
      setVideoFiles([]);
      setError(null);
    }
  }, [editingItem, reset]);

  const handleImagesChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setImageEntries((prev) => {
      const next = [...prev, ...files.map((f) => ({ type: 'file' as const, value: f }))];
      const firstNewIndex = prev.length;
      setCropIndex(firstNewIndex);
      setCropFile(files[0]);
      setCropDialogOpen(true);
      return next;
    });
  };

  const handleCropExisting = async (idx: number) => {
    const entry = imageEntries[idx];
    if (entry?.type !== 'url') return;
    setCropLoading(true);
    try {
      const res = await fetch(entry.value);
      const blob = await res.blob();
      const file = new File([blob], 'image.jpg', { type: blob.type || 'image/jpeg' });
      setCropIndex(idx);
      setCropFile(file);
      setCropDialogOpen(true);
    } catch {
      setError('Could not load image for cropping. Check CORS or try another image.');
    } finally {
      setCropLoading(false);
    }
  };

  const handleVideosChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 2) {
      setError('You can upload a maximum of 2 videos.');
      return;
    }
    setError(null);
    setVideoFiles(files);
  };

  const onSubmit = async (values: ItemFormValues) => {
    setError(null);

    if (videoFiles.length > 2) {
      setError('You can upload a maximum of 2 videos.');
      return;
    }

    const formData = new FormData();
    formData.append('title', values.title);
    formData.append('description', values.description);
    formData.append('categoryId', values.categoryId);
    formData.append('isActive', String(values.isActive));

    const existingPayload = imageEntries.map((e) => (e.type === 'url' ? e.value : null));
    formData.append('existingImages', JSON.stringify(existingPayload));
    imageEntries
      .filter((e): e is { type: 'file'; value: File } => e.type === 'file')
      .forEach((e) => formData.append('images', e.value));

    videoFiles.forEach((file) => {
      if (file.size > 25 * 1024 * 1024) {
        setError('Each video must be less than 25MB.');
        return;
      }
      formData.append('videos', file);
    });

    try {
      if (editingItem) {
        await http.put(`/items/${editingItem._id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await http.post('/items', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      onClose(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save item');
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>{editingItem ? 'Edit Item' : 'New Item'}</DialogTitle>
      <DialogContent dividers>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Title"
                    fullWidth
                    error={!!errors.title}
                    helperText={errors.title?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="categoryId"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Category"
                    fullWidth
                    select
                    error={!!errors.categoryId}
                    helperText={errors.categoryId?.message}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat._id} value={cat._id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Description
              </Typography>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <RichTextEditor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Describe the item…"
                    minHeight={180}
                  />
                )}
              />
              {errors.description && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {errors.description.message}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Images
              </Typography>
              <Button variant="outlined" component="label">
                Select Images
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  onChange={handleImagesChange}
                />
              </Button>
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {imageEntries.map((entry, idx) => (
                  <Box
                    key={entry.type === 'url' ? entry.value : `${idx}-${entry.value.name}`}
                    sx={{ position: 'relative', cursor: 'grab' }}
                    draggable
                    onDragStart={() => setDragImageIndex(idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      if (dragImageIndex === null || dragImageIndex === idx) return;
                      setImageEntries((prev) => {
                        const copy = [...prev];
                        const [moved] = copy.splice(dragImageIndex, 1);
                        copy.splice(idx, 0, moved);
                        return copy;
                      });
                      setDragImageIndex(null);
                    }}
                  >
                    <Box
                      component="img"
                      src={entry.type === 'url' ? entry.value : URL.createObjectURL(entry.value)}
                      alt={entry.type === 'url' ? 'Existing' : entry.value.name}
                      sx={{
                        width: 72,
                        height: 90,
                        objectFit: 'cover',
                        borderRadius: 1
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        display: 'flex',
                        gap: 0.25
                      }}
                    >
                      {entry.type === 'url' && (
                        <IconButton
                          size="small"
                          sx={{ bgcolor: 'white', boxShadow: 1 }}
                          onClick={() => handleCropExisting(idx)}
                          disabled={cropLoading}
                          title="Crop / resize"
                        >
                          <CropIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        sx={{ bgcolor: 'white', boxShadow: 1 }}
                        onClick={() =>
                          setImageEntries((prev) => prev.filter((_, i) => i !== idx))
                        }
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Videos (max 2)
              </Typography>
              <Button variant="outlined" component="label">
                Select Videos
                <input
                  type="file"
                  hidden
                  multiple
                  accept="video/*"
                  onChange={handleVideosChange}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Uploading multiple videos is not recommended. Prefer images for better performance.
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {videoFiles.map((file, idx) => (
                  <Box
                    key={file.name}
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: 'grey.100',
                      cursor: 'grab'
                    }}
                    draggable
                    onDragStart={() => setDragVideoIndex(idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      if (dragVideoIndex === null || dragVideoIndex === idx) return;
                      setVideoFiles((prev) => {
                        const copy = [...prev];
                        const [moved] = copy.splice(dragVideoIndex, 1);
                        copy.splice(idx, 0, moved);
                        return copy;
                      });
                      setDragVideoIndex(null);
                    }}
                  >
                    <Typography variant="caption">{file.name}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>

          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
      {cropDialogOpen && cropFile && (
        <ImageCropDialog
          open={cropDialogOpen}
          file={cropFile}
          onClose={() => {
            setCropDialogOpen(false);
            setCropFile(null);
            setCropIndex(null);
          }}
          onSave={(cropped) => {
            if (cropIndex !== null) {
              setImageEntries((prev) => {
                const copy = [...prev];
                copy[cropIndex] = { type: 'file', value: cropped };
                return copy;
              });
            }
            setCropDialogOpen(false);
            setCropFile(null);
            setCropIndex(null);
          }}
        />
      )}
    </Dialog>
  );
};

